/**
 * @title LPTP extension for the Ciao Prolog Playground
 * 
 * Commands for Ciao-LPTP (see `ciao_lptp` bundle).
 *
 * @author Marco Perez Gonzalez
 * @author The Ciao Development Team
 */

function get_line_indentation(line) {
  const match = line.match(/^(\s*)/);
  return match ? match[1] : "";
}

function indent_text(text, indentation) {
  return text
    .split("\n")
    .map(line => indentation + line)
    .join("\n");
}

function insert_before_closing(lineContent, insertion) {
  const match = lineContent.match(/^(.*?)(\s*)([\).]*)$/);
  if (match) {
    const [_, before, ws, end] = match;
    return `${before}${insertion}${end}`;
  } else {
    return `${lineContent}${insertion}`;
  }
}

function add_by_clause(editor, content, multiline = null) {
  editor.transform_current_line((lineContent) => {
    const byRegex = /\s+by\s+[^).]*?(?=[\s).]*$)/;
    
    // Auto-detect multiline if not specified
    if (multiline === null) {
      multiline = content.includes('\n');
    }
    
    if (multiline) {
      // Format as multiline with proper indentation
      const baseIndent = get_line_indentation(lineContent);
      const contentIndent = baseIndent + "  ";
      const indentedContent = indent_text(content, contentIndent);
      const formattedClause = ` by [\n${indentedContent}\n${baseIndent}]`;
      
      if (byRegex.test(lineContent)) {
        return lineContent.replace(byRegex, formattedClause);
      } else {
        return insert_before_closing(lineContent, formattedClause);
      }
    } else {
      // Format as single line
      const formattedClause = ` by [${content}]`;
      
      if (byRegex.test(lineContent)) {
        return lineContent.replace(byRegex, formattedClause);
      } else {
        return insert_before_closing(lineContent, formattedClause);
      }
    }
  });
}

function insert_by_clause_at_cursor(editor, content) {
  editor.transform_current_line((lineContent, position) => {
    const byRegex = /\s+by\s+(\[[^\]]*\]|[^\[\]()\s.]+)(?=[\s\)\.]*$)/;

    if (byRegex.test(lineContent)) {
      // Replace existing by clause
      return lineContent.replace(byRegex, ` by [${content}]`);
    } else {
      // Insert at cursor position
      const col = position.column;
      const before = lineContent.slice(0, col - 1);
      const after = lineContent.slice(col - 1);
      return `${before} by [${content}]${after}`;
    }
  });
}

/** Send buffer */
export async function send_lptp_buffer(pg) {
  const mod = pg.curr_mod_path();
  if (toplevelCfg.statistics) console.log(`{Checking proofs '${mod}'}`);
  const q = "check_proofs_in_module('" + mod + "')";
  await pg.toplevel.do_query(q, {msg: 'Checking proofs'});
}

/** Get buffer */
export async function get_lptp_buffer(pg) {
  // Step 1: Extract the last block between "======" from toplevel
  const output = pg.toplevel.editor.extract_delimited_block("======");

  if (!output) {
    console.warn("No valid LPTP output block found.");
    return;
  }

  // Step 2: Add the output as a by clause to the current line
  add_by_clause(pg.editor, output);
}

/** Tactics */
export function lptp_tactic(pg, sourceString) {
  insert_by_clause_at_cursor(pg.editor, sourceString);
}

/** Print LPTP definition */
export async function print_lptp_definition(pg) {
  // Define patterns for terminates/succeeds predicates
  const predicatePatterns = [
    /\b(terminates|succeeds)\s+([a-zA-Z0-9_]+\s*\(.*?\))/g
  ];
  
  const pred = pg.editor.get_text_at_cursor(predicatePatterns);

  if (!pred) {
    console.warn("No predicate found at cursor or selection.");
    return;
  }

  const q = `def(${pred})`;
  await pg.toplevel.do_query(q, { msg: 'Printing definition' });
}

/** List LPTP facts */
export async function list_lptp_facts(pg) {
  const pred = pg.editor.get_predicate_name_at_cursor();

  if (!pred) {
    console.warn("No predicate found at cursor or selection.");
    return;
  }

  const q = `facts(${pred})`;
  await pg.toplevel.do_query(q, { msg: 'Listing facts' });
}

/** Mark LPTP */
export async function lptp_mark(pg) {
  const pred = pg.editor.get_predicate_name_at_cursor();

  if (!pred) {
    console.warn("No predicate found at cursor or selection.");
    return;
  }

  const q = `mark(${pred})`;
  await pg.toplevel.do_query(q, { msg: 'Listing facts' });
}
